import { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import moment from 'moment-timezone'
import _ from 'lodash'

import { MenuItem, Box, IconButton, Typography, Switch, Stack, Tooltip } from '@mui/material'

import QrCode2Icon from '@mui/icons-material/QrCode2'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import SaveAltIcon from '@mui/icons-material/SaveAlt'

import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import AddToHomeScreenOutlinedIcon from '@mui/icons-material/AddToHomeScreenOutlined'
import ListOutlinedIcon from '@mui/icons-material/ListOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './template-list-to-excel'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const ActiveTemplates = ({ activeTemplates, archivedTemplates, loadingTemplates, viewDetails, viewPasses, duplicateTemplate, editTemplate, syncPasses, archiveTemplate, unarchiveTemplate, issuePasses, notifyAllPasses }) => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])
  const [dataSource, setDataSource] = useState("Active")
  const [data, setData] = useState(activeTemplates)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const totalLivePassesIssued = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_issued)))
  const totalTestPassesIssued = useMemo(() => _.sum(activeTemplates.map(t => t.test_passes_issued)))
  const totalScannerPasses = useMemo(() => _.sum(activeTemplates.map(t => t.smartix_scanner)))
  const totalLiveActive = useMemo(() => _.sum(activeTemplates.map(t => t.num_live_active_passes)))
  const totalLiveExpired = useMemo(() => _.sum(activeTemplates.map(t => t.num_live_expired_passes)))
  const totalInstalledApple = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_installed_apple)))
  const totalInstalledGoogle = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_installed_google)))
  const totalUnsynced = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_not_synced_apple + t.live_passes_not_synced_google)))

  const navigate = useNavigate()

  useEffect(() => {
    const ds = dataSource === "Active" ? activeTemplates : archivedTemplates
    setData(ds)
  }, [dataSource, activeTemplates, archivedTemplates])

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'Template ID',
      filterVariant: 'autocomplete',
      size: 200,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
    {
      accessorKey: 'name',
      header: 'Template Name',
      filterVariant: 'autocomplete',
      size: 400,
      Cell: ({ cell, row }) => <div className='link' onClick={() => { viewDetails(row.id) }}><OpenInNewIcon className='mr-5 grey' />{cell.getValue()}</div>
    },
    {
      accessorKey: 'group_id',
      header: 'Template Group',
      filterVariant: 'autocomplete',
      size: 100,
      Footer: () => "Totals"
    },
    {
      accessorKey: 'smartix_scanner',
      header: 'Smartix Scanner',
      filterVariant: 'autocomplete',
      size: 20,
      Cell: ({ cell }) => cell.getValue() === 1 ? <QrCode2Icon /> : null,
      muiTableBodyCellProps: {
        align: 'center',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalScannerPasses
    },
    {
      accessorKey: 'live_passes_issued',
      header: 'Issued',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLivePassesIssued
    },
    {
      accessorKey: 'num_live_active_passes',
      header: 'Current',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLiveActive
    },
    {
      accessorKey: 'num_live_expired_passes',
      header: 'Expired',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLiveExpired
    },
    {
      accessorKey: 'live_passes_installed_apple',
      header: <><AppleIcon /> Installed</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalInstalledApple
    },
    {
      accessorKey: 'live_passes_installed_google',
      header: <><AndroidIcon /> Installed</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalInstalledGoogle
    },
    {
      id: "sync",
      accessorFn: (originalRow) => originalRow.live_passes_not_synced_apple + originalRow.live_passes_not_synced_google,
      header: <><SyncOutlinedIcon /> Unsynced</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalUnsynced
    },
    {
      accessorKey: 'test_passes_issued',
      header: 'Test Passes',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalTestPassesIssued
    },
    {
      id: "creation_timestamp",
      accessorFn: (originalRow) => moment(originalRow.creation_timestamp, "YYYYMMDDHHmmss"),
      header: 'Created',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 200
    },
    {
      id: "last_modified_timestamp",
      accessorFn: (originalRow) => moment(originalRow.last_modified_timestamp, "YYYYMMDDHHmmss"),
      header: 'Last Modified',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 200
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      isLoading: loadingTemplates
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    layoutMode: 'semantic',
    ...tableStyles,
    renderRowActionMenuItems: ({ closeMenu, row }) => dataSource === "Active" ?
      [
        <MenuItem key={1} onClick={() => { viewDetails(row.id); closeMenu() }}><OpenInNewIcon className='mr-2' /> Template Details</MenuItem>,
        <MenuItem key={2} onClick={() => { editTemplate(row.id); closeMenu() }}><EditIcon className='mr-2' /> Edit Template</MenuItem>,
        <MenuItem key={3} onClick={() => { duplicateTemplate(row.id); closeMenu() }} disabled={row.original.smartix_scanner === 1}><ContentCopyOutlinedIcon className='mr-2' /> Duplicate Template</MenuItem>,
        <MenuItem key={4} onClick={() => { archiveTemplate(row.id); closeMenu() }}><ArchiveOutlinedIcon className='mr-2' /> Archive Template</MenuItem>,
        <MenuItem key={5} onClick={() => { syncPasses(row.id); closeMenu() }}><SyncOutlinedIcon className='mr-2' /> Synchronise Passes</MenuItem>,
        <MenuItem key={6} onClick={() => { viewPasses(row.id); closeMenu() }}><ListOutlinedIcon className='mr-2' /> View Issued Passes </MenuItem>,
        <MenuItem key={7} onClick={() => { issuePasses(row.id); closeMenu() }}><AddToHomeScreenOutlinedIcon className='mr-2' /> Issue New Passes</MenuItem>,
        <MenuItem key={8} onClick={() => { notifyAllPasses(row.id); closeMenu() }}><EmailOutlinedIcon className='mr-2' /> Notify All Passes</MenuItem>,
      ]
      :
      [
        <MenuItem key={1} onClick={() => { viewDetails(row.id); closeMenu() }}><OpenInNewIcon className='mr-2' /> Template Details</MenuItem>,
        <MenuItem key={2} onClick={() => { editTemplate(row.id); closeMenu() }}><EditIcon className='mr-2' /> Edit Template</MenuItem>,
        <MenuItem key={3} onClick={() => { duplicateTemplate(row.id); closeMenu() }}><ContentCopyOutlinedIcon className='mr-2' /> Duplicate Template</MenuItem>,
        <MenuItem key={4} onClick={() => { unarchiveTemplate(row.id); closeMenu() }}><UnarchiveOutlinedIcon className='mr-2' /> Un-Archive Template</MenuItem>,
        <MenuItem key={6} onClick={() => { viewPasses(row.id); closeMenu() }}><ListOutlinedIcon className='mr-2' /> View Issued Passes </MenuItem>,
      ],
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row gap-5'>
        <IconButton onClick={() => navigate("/create-template")} className='link'><AddCircleOutlineOutlinedIcon /></IconButton>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Archived Templates ({archivedTemplates.length})</Typography>
          <Switch checked={dataSource === "Active"} onChange={e => setDataSource(e.target.checked ? "Active" : "Archived")} />
          <Typography>Active Templates ({activeTemplates.length})</Typography>
        </Stack>
      </div>
    )
  })

  return (
    <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
        <MaterialReactTable table={table} />
        <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="templates" data={{ active: activeTemplates, archived: archivedTemplates }} excelExport={excelExport} />
      </StyledPaper>
    </Box>)
}

export default ActiveTemplates

const numberFormat = value => <span className={value === 0 ? "grey" : ""}>{value?.toLocaleString()}</span>
